import {Correlation, LibraryModalResult, QueryModalResult, Score} from "../types/list";

import { useMemo } from 'react';
import {useListContext} from "../contexts/ListContext";
import {DataType} from "../types/enums";

const classifyExpression = (exp: number): string => {
  if (exp > 5) return "High";
  if (exp < 1) return "Low";
  return "Average";
};

export const useListModalData = (
  queryModalResult: QueryModalResult | LibraryModalResult | null,
  libraryModalResult: LibraryModalResult | null,
  selectedRowModal: Score | Correlation | null
) => {
  const {listState} = useListContext();
  return useMemo(() => {
    if (!queryModalResult || !libraryModalResult || !selectedRowModal) {
      return null;
    }

    /**
     * Helper function to transform gene data (accepts either QueryModalResult or LibraryModalResult)
     * into a standardized format suitable for the GeneInfo modal display for both Score and Correlation data types.
    */
    const buildGeneData = (data: any, isQuery: boolean) => {
      const label = data.original_label;
      const symbol = data.symbol || data.genes?.[0]?.symbol;
      const gene = symbol !== label ? `${symbol} / ${label}` : symbol;
      const expression = classifyExpression(data.expression);
      const essentiality = data.essential ? "Essential" : "Non-essential";
      const fitness = data.fitness === "No Fitness Phenotype" ? "None" : data.fitness || "";
      const fitnessLabel = essentiality === "Essential" ? "Defect" : fitness;
      const mf_var = data.variability === "High" ? "High (var)" : data.variability;
      let fitnessScore;
      if (isQuery) {
        const fitnessKey = `fitness_${data.media}` as keyof typeof data;
        fitnessScore = data[fitnessKey];
      } else {
        fitnessScore = data["fitness_rich"];
      }

      return {
        gene,
        entrezID: data.entrez_id || data.genes?.[0]?.entrez_id,
        symbol,
        description: data.description || data.genes?.[0]?.description,
        uniprot: data.uniprot || data.genes?.[0]?.uniprot,
        expression,
        expressionScore: data.expression?.toFixed?.(2) || "",
        essentiality,
        fitnessLabel,
        fitnessScore: (fitnessScore as number)?.toFixed?.(2) || "",
        fitnessVariability: mf_var,
      };
    };

    // qGI modal
    if (listState.dataType !== DataType.Correlations) {
      const row = selectedRowModal as Score;
      const qgi_score = row.qgi;
      const fdr_score = row.fdr.toExponential(1);
      const label = qgi_score < 0 ? "Negative Interaction" : "Positive Interaction";
      const queryResult = queryModalResult as QueryModalResult;

      return {
        query: buildGeneData(queryResult, true),
        library: buildGeneData(libraryModalResult, false),
        modal: {
          title: `${queryResult.genes[0]?.symbol} ${queryResult.screen_id} — ${libraryModalResult.symbol}`,
          subtitle: `${label} (qGI score: ${qgi_score.toFixed(2)}, FDR < ${fdr_score})`,
        },
      };
    }
    // Profile Similarity (PCC) modal
    else {
      const lib1Result = queryModalResult as LibraryModalResult;
      const row = selectedRowModal as Correlation;
      return {
        query: buildGeneData(queryModalResult, false),
        library: buildGeneData(libraryModalResult, false),
        modal: {
          title: `${lib1Result.symbol} — ${libraryModalResult.symbol}`,
          subtitle: `Profile Similarity (PCC: ${row.pcc.toFixed(2)})`,
        },
      };
    }
  }, [queryModalResult, libraryModalResult, selectedRowModal]);
};
